import { useState, useCallback, useEffect, useRef } from 'react';

type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right';

interface TooltipOptions {
  placement?: TooltipPlacement;
  delay?: number;
  offset?: number;
  disabled?: boolean;
}

interface TooltipState {
  isVisible: boolean;
  placement: TooltipPlacement;
  position: { x: number; y: number };
  arrowPosition: { x: number; y: number };
}

const defaultOptions: TooltipOptions = {
  placement: 'top',
  delay: 500,
  offset: 8,
  disabled: false
};

export function useTooltip(options: TooltipOptions = {}) {
  const config = { ...defaultOptions, ...options };
  const [state, setState] = useState<TooltipState>({
    isVisible: false,
    placement: config.placement!,
    position: { x: 0, y: 0 },
    arrowPosition: { x: 0, y: 0 }
  });

  const triggerRef = useRef<HTMLElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isHovering = useRef(false);

  // Calculate tooltip position
  const calculatePosition = useCallback(() => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    let placement = config.placement!;
    let x = 0;
    let y = 0;
    let arrowX = 0;
    let arrowY = 0;

    // Calculate initial position based on placement
    switch (placement) {
      case 'top':
        x = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
        y = triggerRect.top - tooltipRect.height - config.offset!;
        arrowX = tooltipRect.width / 2 - 6; // 6px is half of arrow width
        arrowY = tooltipRect.height;
        break;
      case 'bottom':
        x = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;
        y = triggerRect.bottom + config.offset!;
        arrowX = tooltipRect.width / 2 - 6;
        arrowY = -6; // Negative to position above tooltip
        break;
      case 'left':
        x = triggerRect.left - tooltipRect.width - config.offset!;
        y = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
        arrowX = tooltipRect.width;
        arrowY = tooltipRect.height / 2 - 6;
        break;
      case 'right':
        x = triggerRect.right + config.offset!;
        y = triggerRect.top + triggerRect.height / 2 - tooltipRect.height / 2;
        arrowX = -6;
        arrowY = tooltipRect.height / 2 - 6;
        break;
    }

    // Check for collisions and adjust placement
    const collisions = {
      top: y < 0,
      bottom: y + tooltipRect.height > viewportHeight,
      left: x < 0,
      right: x + tooltipRect.width > viewportWidth
    };

    // Flip placement if there's a collision
    if (placement === 'top' && collisions.top && !collisions.bottom) {
      placement = 'bottom';
      y = triggerRect.bottom + config.offset!;
      arrowY = -6;
    } else if (placement === 'bottom' && collisions.bottom && !collisions.top) {
      placement = 'top';
      y = triggerRect.top - tooltipRect.height - config.offset!;
      arrowY = tooltipRect.height;
    } else if (placement === 'left' && collisions.left && !collisions.right) {
      placement = 'right';
      x = triggerRect.right + config.offset!;
      arrowX = -6;
    } else if (placement === 'right' && collisions.right && !collisions.left) {
      placement = 'left';
      x = triggerRect.left - tooltipRect.width - config.offset!;
      arrowX = tooltipRect.width;
    }

    // Adjust position to stay within viewport
    if (placement === 'top' || placement === 'bottom') {
      if (collisions.left) {
        const diff = -x;
        x = 8; // Minimum margin
        arrowX = Math.max(8, arrowX - diff);
      } else if (collisions.right) {
        const diff = x + tooltipRect.width - viewportWidth;
        x = viewportWidth - tooltipRect.width - 8;
        arrowX = Math.min(tooltipRect.width - 8, arrowX + diff);
      }
    } else {
      if (collisions.top) {
        const diff = -y;
        y = 8;
        arrowY = Math.max(8, arrowY - diff);
      } else if (collisions.bottom) {
        const diff = y + tooltipRect.height - viewportHeight;
        y = viewportHeight - tooltipRect.height - 8;
        arrowY = Math.min(tooltipRect.height - 8, arrowY + diff);
      }
    }

    // Add scroll offset
    x += scrollX;
    y += scrollY;

    setState(prev => ({
      ...prev,
      placement,
      position: { x, y },
      arrowPosition: { x: arrowX, y: arrowY }
    }));
  }, [config.placement, config.offset]);

  // Show tooltip
  const show = useCallback(() => {
    if (config.disabled) return;

    isHovering.current = true;
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      if (isHovering.current) {
        setState(prev => ({ ...prev, isVisible: true }));
        // Calculate position after state update
        setTimeout(calculatePosition, 0);
      }
    }, config.delay);
  }, [config.disabled, config.delay, calculatePosition]);

  // Hide tooltip
  const hide = useCallback(() => {
    isHovering.current = false;
    
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    setState(prev => ({ ...prev, isVisible: false }));
  }, []);

  // Event handlers
  const handleMouseEnter = useCallback(() => {
    show();
  }, [show]);

  const handleMouseLeave = useCallback(() => {
    hide();
  }, [hide]);

  const handleFocus = useCallback(() => {
    show();
  }, [show]);

  const handleBlur = useCallback(() => {
    hide();
  }, [hide]);

  // Handle window resize and scroll
  useEffect(() => {
    if (state.isVisible) {
      const handleResize = () => calculatePosition();
      const handleScroll = () => calculatePosition();

      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll, true);

      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [state.isVisible, calculatePosition]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Touch device support
  const handleTouchStart = useCallback((e: TouchEvent) => {
    e.preventDefault();
    show();
  }, [show]);

  const handleTouchEnd = useCallback(() => {
    setTimeout(hide, 2000); // Auto-hide after 2 seconds on touch
  }, [hide]);

  return {
    triggerRef,
    tooltipRef,
    isVisible: state.isVisible,
    placement: state.placement,
    position: state.position,
    arrowPosition: state.arrowPosition,
    show,
    hide,
    handleMouseEnter,
    handleMouseLeave,
    handleFocus,
    handleBlur,
    handleTouchStart,
    handleTouchEnd
  };
} 